"use client";
import { useTranslations } from "next-intl";
import { IBaseLabelValue } from "@/interfaces";
import { CustomButton, FlexBetween, FlexCenter, FlexCol } from "../../ui";
import { cn } from "@/lib";
import { useRouter } from "next/navigation";
import ResultContent from "../../resultContent";
import { routes } from "@/routes";
import DownloadPdf from "../../pdfTemplates/downloadPdf";
import DetailCreditCard from "../../detailCreditCard";
import DetailInquiryPdf from "../../pdfTemplates/detailInquiryPdf";
import { useRef } from "react";
import { IRawPurchasedCardsInquiryResData } from "@/interfaces/inquiries/bank";

const CardInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedCardsInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const { bank, ownerName, type, depositNumber, number } = data.data.data;

  const resultContent: IBaseLabelValue[] = [
    {
      label: t("cardInquiry.ownerName"),
      value: ownerName,
    },
    {
      label: t("cardInquiry.bankName"),
      value: bank,
    },
    {
      label: t("cardInquiry.cardType"),
      value: type,
    },
    {
      label: t("cardInquiry.depositNumber"),
      value: depositNumber,
    },
  ];

  return (
    <div className="shadow-main from-emerald-mint relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-8 text-center font-bold lg:text-lg">
          {t("cardInquiry.title")}
        </h2>
        <div className="mx-auto mb-8 sm:w-3/4">
          <DetailCreditCard
            variant="credit"
            owners={ownerName}
            cardValues={{
              label: "شماره کارت",
              value: number,
            }}
            footer={() => (
              <>
                <div className="flex w-full justify-between">
                  <div className="flex flex-col items-center">
                    <span className="text-xs font-light">شماره حساب</span>
                    <span
                      className="md:text-md text-sm font-semibold"
                      dir="ltr"
                    >
                      {depositNumber}
                    </span>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-xs font-light">نوع کارت</span>
                    <span className="md:text-md text-sm font-semibold">
                      {type}
                    </span>
                  </div>
                </div>
              </>
            )}
          />
        </div>

        {/* PDF Download Options */}
        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center font-bold lg:text-lg">
            {t("cardInquiry.resultTitle")}
          </h2>
          <DownloadPdf
            id="card-inquiry-download-pdf"
            resultContent={resultContent}
            fileName="کارت-بانکی"
            tableTitle="کارت بانکی"
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
          />
        </FlexBetween>

        {/* This div will be used for PDF generation - DetailCreditCard style */}
        <div
          ref={pdfContentRef}
          style={{
            position: "fixed",
            left: "-9999px",
            top: "-9999px",
            width: "800px",
            fontFamily: "IRANYekan, sans-serif",
            margin: "2rem auto",
          }}
        >
          <DetailInquiryPdf
            code={data.code}
            subtitle="استعلام کارت بانکی"
            displayComponent={{
              variant: "credit",
              mainValue: {
                label: "شماره کارت",
                value: number,
              },
              ownerName,
              bankName: bank,
              additionalFields: [
                {
                  label: "شماره حساب",
                  value: depositNumber,
                },
                {
                  label: "نوع کارت",
                  value: type,
                },
              ],
            }}
            resultContent={resultContent}
            showResultSection={true}
          />
        </div>

        <ResultContent content={resultContent} />

        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            useHtmlToPdf={true}
            id="card-inquiry-download-pdf"
            htmlContentRef={pdfContentRef}
            fileName="کارت-بانکی"
            tableTitle="کارت بانکی"
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-card-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-card-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Card_Inquiry.rewritePath)
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
    </div>
  );
};

export default CardInquiryDetails;
